import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { uniqBy } from 'lodash'

import { ATTENDEE_CHECK_IN } from '../../../../../helpers'
import { BOOKING } from '../../../../../reduxs'
import AttendeeCheckInModal from './AttendeeCheckInModal'
import CheckinToast from './CheckinToast'
import { getCheckInWindow, getGuestLabel, isDeclined } from './checkIn'
import { AttendeeCheckInButtonStyled } from './styles'

const CLOCK_REFRESH_MS = 10000

const ERROR_MESSAGE = {
  checkInEarly: ATTENDEE_CHECK_IN.OUTSIDE_TIME,
  checkInLate: ATTENDEE_CHECK_IN.OUTSIDE_TIME,
  checkInAllForbidden: ATTENDEE_CHECK_IN.FORBIDDEN
}

const toGuests = (rawGuests = []) => uniqBy(
  [...rawGuests].sort((a, b) => Number(b.ownership == 1) - Number(a.ownership == 1)),
  'username'
).map(guest => ({ ...guest, isCheckedIn: Boolean(guest.checkInTime) }))

function AttendeeCheckIn(props) {
  const {
    bookingId,
    language,
    username,
    colorTheme,
    getBookingCheckIn,
    checkInBooking,
    checkInAllBooking,
  } = props

  const [detail, setDetail] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [toast, setToast] = useState(null)
  const [nowMs, setNowMs] = useState(Date.now())

  const fetchDetail = async () => {
    const res = await getBookingCheckIn(bookingId)
    if (res && res.status) setDetail(res.data)
  }

  useEffect(() => {
    if (bookingId) fetchDetail()
  }, [bookingId])

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), CLOCK_REFRESH_MS)
    return () => clearInterval(timer)
  }, [])

  const isOwner = Boolean(detail) && detail.owner && detail.owner.username === username
  if (!isOwner || !detail.checkInRequired) return null

  const guests = toGuests(detail.guest)
  const checkedInCount = guests.filter(guest => guest.isCheckedIn).length
  const expectedCount = guests.length - guests.filter(isDeclined).length
  const isComplete = checkedInCount >= expectedCount
  const checkInWindow = getCheckInWindow(detail, nowMs)

  const showError = (res) => {
    const messageKey = res && res.data && res.data.messageKey
    const message = ERROR_MESSAGE[messageKey] || ATTENDEE_CHECK_IN.ERROR_MSG
    setToast({ id: Date.now(), type: 'error', title: ATTENDEE_CHECK_IN.ERROR_TITLE[language], message: message[language] })
  }

  const handleCheckInAll = async () => {
    const res = await checkInAllBooking(bookingId)

    if (res && res.status) {
      const count = (res.data && res.data.checkedInCount) || 0
      await fetchDetail()
      setToast({
        id: Date.now(),
        type: 'success',
        title: ATTENDEE_CHECK_IN.CHECK_IN_SUCCESS[language],
        message: count > 0
          ? ATTENDEE_CHECK_IN.CHECK_IN_ALL_SUCCESS_MSG[language](count)
          : ATTENDEE_CHECK_IN.CHECK_IN_ALL_NOTHING_MSG[language]
      })
      return
    }

    showError(res)
  }

  const handleCheckInGuest = async (guest) => {
    const res = await checkInBooking({ roomId: detail.roomId, pin: guest.pincode })
    const messageKey = res && res.data && res.data.messageKey

    if ((res && res.status) || messageKey === 'alreadyCheckedIn') {
      await fetchDetail()
      setToast({
        id: Date.now(),
        type: 'success',
        title: ATTENDEE_CHECK_IN.CHECK_IN_SUCCESS[language],
        message: ATTENDEE_CHECK_IN.CHECK_IN_GUEST_SUCCESS_MSG[language](getGuestLabel(guest))
      })
      return
    }

    showError(res)
  }

  return (
    <>
      <AttendeeCheckInButtonStyled
        colorTheme={colorTheme}
        className={isComplete ? 'complete' : ''}
        onClick={() => setIsModalOpen(true)}
      >
        {ATTENDEE_CHECK_IN.TITLE[language]}
        <span className='attendee-check-in-count'>
          {isComplete && '✓ '}{checkedInCount}/{expectedCount}
        </span>
      </AttendeeCheckInButtonStyled>

      {isModalOpen && (
        <AttendeeCheckInModal
          isVisible
          guests={guests}
          isCheckInDisabled={!checkInWindow.isOpen}
          checkInWindowText={checkInWindow.text}
          colorTheme={colorTheme}
          onCheckInAll={handleCheckInAll}
          onCheckInGuest={handleCheckInGuest}
          onClose={() => setIsModalOpen(false)}
          language={language}
        />
      )}

      {toast && (
        <CheckinToast
          key={toast.id}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </>
  )
}

const mapStateToProps = ({ accountReducer }) => {
  return {
    language: accountReducer.language,
    username: accountReducer.user.username,
    colorTheme: accountReducer.user.colorThemePalette,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getBookingCheckIn: (bookingId) => dispatch(BOOKING.action.getBookingCheckIn(bookingId)),
    checkInBooking: (params) => dispatch(BOOKING.action.checkInBooking(params)),
    checkInAllBooking: (bookingId) => dispatch(BOOKING.action.checkInAllBooking(bookingId)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AttendeeCheckIn)
